import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { Language } from 'enums/types';
import type { NavigationLink, NavigationSection } from 'components/header/header';
import useOnClickOutside from 'hooks/useOnClickOutside';
import accessibleDropdownKeyboardHandler from 'helpers/accessibleDropdownKeyboardHandler';
import { isEdge, isIE } from '../../browserHelper';
import * as style from './menu.module.scss';

interface MenuProps {
	navigation: NavigationSection[];
}

interface MenuDropdownProps extends NavigationSection {
	selectedDropdown: number;
	setSelectedDropdown: React.Dispatch<React.SetStateAction<number>>;
	index: number;
}

const MenuDropdown = ({ title, links, selectedDropdown, setSelectedDropdown, index }: MenuDropdownProps) => {
	const { formatMessage, locale } = useIntl();

    const [isOpen, setIsOpen] = useState(false);
	const [activeLink, setActiveLink] = useState(-1);

	const containerRef = useRef<HTMLLIElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const linkRefs = useRef<HTMLAnchorElement[]>([]);
	useOnClickOutside(containerRef, () => setIsOpen(false));

	useEffect(() => {
		if (!isOpen) {
			setActiveLink(-1);
		}
	}, [isOpen]);

	useEffect(() => {
		if (selectedDropdown === index && buttonRef.current) {
			buttonRef.current.focus();
		} else {
			setIsOpen(false);
		}
	}, [index, selectedDropdown]);

	return (
		<li
			className={classnames(
				style.navLink,
				{
					[style.active]: isOpen,
					[style.linkStyleForArabicLang]: locale === Language.Arabic,
					[style.linkStyleForPolishLang]: locale === Language.Polish,
				}
			)}
			ref={containerRef}
			key={formatMessage(title)}
			onFocus={() => setSelectedDropdown(index)}
			onKeyDown={(e) => {
				if (!buttonRef.current || !linkRefs.current) {
					return;
				}
				accessibleDropdownKeyboardHandler(e, buttonRef.current, linkRefs.current, isOpen, setIsOpen, activeLink, setActiveLink)
			}}
		>
			<button
				className={style.openButton}
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				onFocus={() => setActiveLink(-1)}
				ref={buttonRef}
				aria-haspopup={true}
				aria-expanded={isOpen}
			>
				<span>
					<FormattedMessage {...title} />
				</span>
				<svg className={classnames(style.arrow, { [style.arrowMarginForArabicLang]: locale === Language.Arabic })} xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7">
					<path d="M6.432 6.114a1 1 0 0 1-1.519 0L1.087 1.651A1 1 0 0 1 1.847 0h7.651a1 1 0 0 1 .76 1.65z" />
				</svg>
			</button>
			<div className={style.popoverWrapper}>
				<div>
					<ul className={classnames({ [style.show]: isOpen }, style.popoverBox, style.subMenu, locale === Language.Deutsch && style.fitContent)}>
						{links.map((link: NavigationLink, linkIndex: number) => link.title && (
								<li
									key={link.link ?? link.externalLink ?? `nav-link-${link.title.id}`}
									className={classnames({ [style.textRight]: locale === Language.Arabic })}
								>
									{link.link && (
										<Link
											to={isIE || isEdge ? encodeURI(link.link) : link.link}
											className={classnames(style.link, {
												[style.textRight]: locale === Language.Arabic
											}, locale !== Language.Deutsch && style.maxWidth)}
											onFocus={() => setActiveLink(linkIndex)}
											onClick={() => setIsOpen(false)}
											ref={((instance: HTMLAnchorElement) => {
												if (linkIndex === 0) {
													linkRefs.current.length = 0;
												}
												linkRefs.current[linkIndex] = instance;
											}) as any}
										>
											<FormattedMessage {...link.title} />
										</Link>
									)}
									{link.externalLink && (
										<a
											className={style.link}
											href={link.externalLink}
											target="_blank"
											rel="noopener noreferrer"
											onFocus={() => setActiveLink(linkIndex)}
											ref={(instance: HTMLAnchorElement) => {
												if (linkIndex === 0) {
													linkRefs.current.length = 0;
												}
												linkRefs.current[linkIndex] = instance;
											}}
										>
											<FormattedMessage {...link.title} />
										</a>
									)}
								</li>
							)
						)}
					</ul>
				</div>
			</div>
		</li>
	)
};

const Menu = ({ navigation }: MenuProps) => {
	const [selectedDropdown, setSelectedDropdown] = useState(-1)

	const onKeyboardEvent = (event: React.KeyboardEvent<HTMLElement>) => {
		if (selectedDropdown < 0) {
			return;
		}
		switch (event.key) {
			case 'ArrowLeft':
				if (selectedDropdown > 0) {
					setSelectedDropdown(selectedDropdown - 1);
				}
				break;
			case 'ArrowRight':
				if (selectedDropdown < navigation.length - 1) {
					setSelectedDropdown(selectedDropdown + 1);
				}
				break;
			default:
				break;
		}
	}

	return (
		<nav className={style.nav} aria-label="Main" onKeyDown={(e) => onKeyboardEvent(e)}>
			<ul className={classnames(style.linksList)}>
				{navigation.map((item: NavigationSection, sectionIndex: number) => item && (
					<MenuDropdown
						title={item.title}
						links={item.links}
						selectedDropdown={selectedDropdown}
						setSelectedDropdown={setSelectedDropdown}
						index={sectionIndex}
						key={item.title.id}
					/>
				))}
			</ul>
		</nav>
	);
};

export default Menu;
